import type { ObjectMetadata } from '@olytic/foundation';

import { OBJECT_DEFS } from '../../objects/objectDefs';
import { asDate, asLabel, column, createdByField, field as plainField, lastModifiedByField, listColumnsOf, lookup, related, type Row, schemaRecord, text } from './kit';

const def = OBJECT_DEFS.audiences;

const contactMembers = related('members', 'Members', 'contacts', [
  column('name', 'Name', { primary: true }),
  column('account_name', 'Account', { link: lookup('accounts', 'account_id') }),
  column('added_at', 'Added', { format: asDate }),
]);

const accountMembers = related('members', 'Members', 'accounts', [
  column('name', 'Name', { primary: true }),
  column('added_at', 'Added', { format: asDate }),
  column('added_reason', 'Reason', { format: asLabel }),
]);

/**
 * Its fields come from the database (the server's SCHEMA_OBJECTS), shown by
 * their type; any column not placed below appears in Other Fields (MOTHER-307).
 * Members is a count of the members not removed. The membership rule, its
 * refresh interval and the metadata are no columns (JSON and an interval):
 * the record reads them, and they show as they come, as before.
 */
const field = schemaRecord('audiences');

const details = [
  field('name', 'Audience Name'),
  field('entity_type', 'Members Are', { format: (v) => (v === 'account' ? 'Accounts' : v === 'contact' ? 'Contacts' : text(v)) }),
  field('membership_type', 'Membership'),
  field('member_count', 'Members'),
  field('tags', 'Tags'),
  field('description', 'Description', { wide: true }),
  field('auto_refresh_enabled', 'Refreshes Automatically'),
  plainField('refresh_interval', 'Refresh Interval'),
  field('last_refreshed_at', 'Last Refreshed'),
  plainField('criteria', 'Criteria', { wide: true }),
  field('created_at', 'Created'),
  field('updated_at', 'Last Modified'),
  plainField('metadata', 'Metadata', { wide: true }),
  createdByField(),
  lastModifiedByField(),
];

/** An audience: a named list of contacts or of accounts. Rationale: audiences.md. */
export const audiences: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Audience',
  plural: def.label,
  icon: def.icon,
  listColumns: listColumnsOf(def, details),
  details,
  sections: [
    { key: 'identity', fields: ['name', 'entity_type', 'membership_type', 'member_count', 'tags', 'description'] },
    { key: 'refresh', label: 'Membership Rule', fields: ['auto_refresh_enabled', 'refresh_interval', 'last_refreshed_at', 'criteria'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['created_at', 'created_by_name', 'updated_at', 'last_modified_by_name', 'metadata'] },
  ],
  related: [contactMembers],
  titleOf: (row) => text(row.name),
  searchKeys: ['name', 'description'],
};

/**
 * An audience holds contacts or accounts (`entity_type`), so its members link
 * to one or the other; an account audience's members have no account column.
 */
export function audienceFor(record: Row): ObjectMetadata<Row> {
  return record.entity_type === 'account' ? { ...audiences, related: [accountMembers] } : audiences;
}
